import clsx from 'clsx';
import { RadioButton as PrimereactRadioButton } from 'primereact/radiobutton';
import { forwardRef, ReactNode, Ref } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
    InputWrapper,
    InputWrapperSharedProps,
} from '../InputWrapper/InputWrapper';
import { RadioButton, type RadioButtonProps } from '../RadioButton/RadioButton';
import {
    buildRhfRules,
    warnAboutRegisterSpread,
    type FieldValidate,
} from '../../_utils/rhf-ref';

export interface RadioOption<T> {
    label: string;
    value: T;
}

export type RadioButtonGroupProps<T> = RadioButtonProps &
    InputWrapperSharedProps & {
        name: string;
        options?: RadioOption<T>[];
        verticalOptions?: boolean;
        isDisabled?: (option: RadioOption<T>) => boolean;
        getOptionLabel?: (option: RadioOption<T>) => ReactNode;
        getOptionTooltip?: (option: RadioOption<T>) => string;
        /**
         * Custom react-hook-form validation function
         */
        validate?: FieldValidate;
    };

function sanitizedLabel(label: string): string {
    return label.replace(/[^a-zA-Z0-9]+/g, '_');
}

function RadioButtonGroupComponent<T>(
    props: RadioButtonGroupProps<T>,
    ref: Ref<PrimereactRadioButton>,
) {
    const {
        name,
        checked,
        value: valueProps,
        options,
        verticalOptions,
        onChange,
        bulkMode,
        onCheckedChange,
        validate,
        'data-testid': dataTestId,
        ...restProps
    } = props;

    const testId = dataTestId || name;
    const formContext = useFormContext();

    const isControlled = typeof checked !== 'undefined';
    const isRHFControlled = !isControlled && Boolean(formContext && name);
    warnAboutRegisterSpread({
        componentName: 'RadioButtonGroup',
        isRHFControlled,
        onChange,
        ref,
    });

    if (isControlled || !formContext) {
        return (
            <InputWrapper {...restProps}>
                <RadioButton
                    {...restProps}
                    invalid={Boolean(
                        restProps.invalid || restProps.errorMessage,
                    )}
                    ref={ref}
                />
            </InputWrapper>
        );
    }

    return (
        <Controller
            name={name}
            control={formContext.control}
            rules={buildRhfRules({
                required: props.required,
                bulkMode,
                validate,
            })}
            render={({ field, fieldState }) => {
                const value = field?.value || valueProps;
                const optionsClassName = clsx(
                    'flex',
                    verticalOptions ? 'flex-column gap-2' : 'gap-3',
                );

                return (
                    <InputWrapper
                        field={field}
                        fieldState={fieldState}
                        {...restProps}
                    >
                        <div className={optionsClassName} data-testid={testId}>
                            {options?.map((option) => (
                                <div
                                    className="flex align-items-center gap-2"
                                    key={`${option.label}-${value}`}
                                >
                                    <RadioButton
                                        {...field}
                                        pt={{
                                            input: {
                                                'data-testid': `${testId}-${sanitizedLabel(option.label)}`,
                                            },
                                        }}
                                        name={sanitizedLabel(option.label)}
                                        value={option.value}
                                        checked={value === option.value}
                                        invalid={Boolean(
                                            fieldState.invalid ||
                                                props.invalid ||
                                                props.errorMessage,
                                        )}
                                        toggleable={props.toggleable}
                                        disabled={
                                            props.isDisabled
                                                ? props.isDisabled(option)
                                                : field.disabled
                                        }
                                        tooltip={
                                            props.getOptionTooltip
                                                ? props.getOptionTooltip(option)
                                                : props.tooltip
                                        }
                                        onChange={(e) => {
                                            if (onChange) {
                                                onChange(e);
                                            }
                                        }}
                                        onCheckedChange={(checked) => {
                                            field?.onChange(
                                                checked
                                                    ? option.value
                                                    : undefined,
                                            );
                                            onCheckedChange?.(checked);
                                        }}
                                    />
                                    <span>
                                        {props.getOptionLabel
                                            ? props.getOptionLabel(option)
                                            : option.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </InputWrapper>
                );
            }}
        />
    );
}

export const RadioButtonGroup = forwardRef(RadioButtonGroupComponent);
